import { Component, OnInit, OnDestroy, Output, EventEmitter, PLATFORM_ID, Inject, signal, Signal, AfterViewInit, ChangeDetectorRef, input, Input, output } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { Subject, firstValueFrom, interval, takeUntil } from 'rxjs';
import { FuseSplashScreenService } from '../../../@fuse/splash-screen/splash-screen.service';
import { TranslateModule } from '@ngx-translate/core';
import { TranslationService } from '../../services/translation.service';
import { I } from '@angular/cdk/keycodes';
import { ClientService } from '../../services/client-service';
import { access } from 'node:fs';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { FormatTimePipe } from '../../pipes/format-time.pipe';

// Enum for search status
export enum SearchStatus {
  STARTED = 'started',        // Pending search ongoing
  COMPLETED = 'completed',    // Status updated while still in search
  FINISHED = 'finished',      // Search finished
  BROADCAST = 'broadcast',    // Only for departures (sent to everyone)
  CANCELLED = 'cancelled'     // Search was cancelled
}

interface Driver {
  offerId: string;
  ticketId: string;
  name: string;
  rating: number;
  image: string;
  plate: string;
  taxiNum: string;
  taxiType: number;
  fare: number;
  distance: number;
  expiresAt: string;
  pc: string;
  confirmUrl: string;
  confirmBody: any;
}

interface Location {
  name: string;
  category: string;
  distance: number;
  lat: number;
  lng: number;
}

interface TripData {
  driverFullName: string;
  driverPhoneNumber?: string;
  driverPermis?: string;
  driverCarModel: string;
  driverCarPlateNumber?: string;
  taxi_num?: string;
  taxiType?: number;
  driverAvgRatings?: number;
  pickup: string;
  destination: string;
  passengers: number;
  luggage: number;
  finalPrice: number;
}

@Component({
  selector: 'app-list-drivers-accepted',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatButtonModule, TranslateModule, FormatTimePipe],
  templateUrl: './list-drivers-accepted.component.html',
  styleUrls: ['./list-drivers-accepted.component.scss']
})
export class ListDriversAcceptedComponent implements OnInit, OnDestroy,AfterViewInit {
  // Outputs
  @Output() tripDataSelected = new EventEmitter<TripData>();

  @Output() clearAcceptedDrivers = new EventEmitter<void>();
  @Output() retourToHome = new EventEmitter<void>();




  // Trip Data
  @Input()
  pickup: string = '';
  @Input()
  destination: string = '';
  passengers: number = 1;
  luggage: number = 0;
  tripData: TripData | null = null;

  currentTripStatus = signal<number>(0);

  // Drivers
  @Input()
  listDriversAccepted: Driver[] = [];

  visibleNonRejectedDrivers: Driver[] = [];
  rejectedDriverIds: Set<string> = new Set();

  // Search
  isSearching: boolean = true;
  @Input()
  searchTimeTotal: number = 0;  
  @Input()
  currentSearchStatus: SearchStatus = SearchStatus.STARTED; // Current search status

  searchTimeRemaining: number = 0;
  visibleDriversCount: number = 0;
  retryCount: number = 0; // Compteur de tentatives
  maxRetries: number = 3; // Nombre maximum de tentatives

  // Price Adjustment
  globalTip: number = 0;
  showPriceAdjuster: boolean = true;
  
  // Custom Price Input
  customPriceAmount: number | null = null;
  showCustomPriceInput: boolean = false;
  minAmount: number = 0;

  // UI States
  isProcessing: boolean = false;
  showLocationPicker: string | null = null;
  searchQuery: string = '';
  ticketId: string = '';
  originalFare: number = 0;

  errorMessage: string = '';

  // Locations
  allLocations: Location[] = [
    { name: 'Aéroport Marrakech', category: 'Aéroport', distance: 8.5, lat: 31.6295, lng: -8.0089 },
    { name: 'Gare Routière', category: 'Transport', distance: 2.3, lat: 31.6496, lng: -8.0089 },
    { name: 'Place Jemaâ el-Fnaa', category: 'Point d\'intérêt', distance: 1.2, lat: 31.6295, lng: -7.9891 },
    { name: 'Palais de la Bahia', category: 'Musée', distance: 3.1, lat: 31.6217, lng: -7.9939 },
    { name: 'Souk Medina', category: 'Marché', distance: 0.5, lat: 31.6308, lng: -7.9858 }
  ];

  filteredLocations: Location[] = [];
  private destroy$ = new Subject<void>();
  isBrowser: boolean = false;
  
  // Public reference to ChangeDetectorRef for template usage
  get cd(): ChangeDetectorRef {
    return this._changeDetectorRef;
  }

  constructor(
    private _splashScreenService: FuseSplashScreenService,
    private _fuseConfirmationService: FuseConfirmationService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private translateService: TranslationService,
    private _changeDetectorRef: ChangeDetectorRef,
    private clientService: ClientService,
  ) {}

  async ngOnInit(): Promise<void> {
        this._splashScreenService.hide();
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.searchTimeRemaining = this.searchTimeTotal;
    this.visibleDriversCount=this.listDriversAccepted.length;
    console.log("List Drivers Accepted Component initialized with drivers:", this.listDriversAccepted);
    if(this.searchTimeRemaining==0){
      this.isSearching = false;
            this._changeDetectorRef.markForCheck();
    }
    await this.initFromActiveTrips();

      // S'abonner à l'événement d'annulation
    this.clientService.cancelSearch$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ticketId: string) => {
        console.log('Recherche annulée pour:', ticketId);
        // Faire quelque chose quand la recherche est annulée
              this.setSearchStatus(SearchStatus.FINISHED);
      this.isSearching = false;
      this._changeDetectorRef.markForCheck();
      clearTimeout(this.searchTimeOutput);
      });




    if(!this.isBrowser) {
      return;
    }



  }

  async initFromActiveTrips(){
        let res=await firstValueFrom(this.clientService.getActiveTrip())
    console.log("Active trip data in accepted drivers component",res);
    this.passengers=res.ticket?.passengers_nb || 1;
    this.luggage=res.ticket?.luggage_nb || 0;
    if(res.success && res.ticket){
      let ticket= res.ticket
      this.minAmount=ticket.fare;
      this.originalFare=ticket.originalFare;
      this.globalTip=ticket.fare - ticket.originalFare;
      console.log("Original fare set to",this.originalFare,'min amount set to',this.minAmount);
      this.customPriceAmount= ticket.fare;
      this.ticketId= res.activeTripId;
    }
  }
  ngAfterViewInit(): void {
    if(!this.isBrowser) {
      return;
    }

    this.initializeSearch();
    this.setupLocationData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

   searchTimeOutput: any;

  private initializeSearch(): void {
    // Set search status to STARTED
    this.setSearchStatus(SearchStatus.STARTED);

    // Initialize drivers
    this.updateVisibleDrivers();
    this.searchTimeOutput = setTimeout(() => {
      // When search finishes, set status to FINISHED
      this.setSearchStatus(SearchStatus.FINISHED);
      this.isSearching = false;
      this._changeDetectorRef.markForCheck();
    }, this.searchTimeTotal * 1000);

    let searchInterval = setInterval(() => {
      // Update status to COMPLETED when drivers are found
      if (this.visibleDriversCount > 0 && this.currentSearchStatus === SearchStatus.STARTED) {
        this.setSearchStatus(SearchStatus.COMPLETED);
      }
      
      this.updateVisibleDrivers();
      this.searchTimeRemaining--;
      this.visibleDriversCount = this.listDriversAccepted.length;
      this._changeDetectorRef.markForCheck();
      if (this.searchTimeRemaining <= 0) {
        clearInterval(searchInterval);
      }

    }, 1000);
  }

  private setupLocationData(): void {
    this.filteredLocations = this.allLocations;
  }

  updateVisibleDrivers(): void {
    this.visibleNonRejectedDrivers = this.listDriversAccepted
      .filter(driver => !this.rejectedDriverIds.has(driver.offerId))
      .slice()
      .reverse(); // Nouveau à ancien
    // console.log('Visible Drivers Updated:', this.visibleNonRejectedDrivers);
    this._changeDetectorRef.markForCheck();
  }

  handleRejectDriver(offerId: string): void {
    this.rejectedDriverIds.add(offerId);

    this.listDriversAccepted = this.listDriversAccepted.filter(driver => driver.offerId !== offerId);
    this.visibleDriversCount = this.listDriversAccepted.length;
    

    this.updateVisibleDrivers();
    this._changeDetectorRef.markForCheck();
  }

  isDriverRejected(driver: Driver): boolean {
    return this.rejectedDriverIds.has(driver.offerId);
  }

  async handleSelectDriver(driver: Driver): Promise<void> {
    this.isProcessing = true;



    let res= await firstValueFrom(this.clientService.acceptDriverByClient(driver.ticketId,driver.offerId));
    console.log("Accept driver response",res);

    this.isProcessing = false  
    this._changeDetectorRef.markForCheck();

  }

  handleLocationSelect(location: Location): void {
    if (this.showLocationPicker === 'pickup') {
      this.pickup = location.name;
    } else if (this.showLocationPicker === 'destination') {
      this.destination = location.name;
    }
    this.showLocationPicker = null;
    this.searchQuery = '';
    this._changeDetectorRef.markForCheck();
  }

  openLocationPicker(type: 'pickup' | 'destination'): void {
    this.showLocationPicker = type;
    this.filteredLocations = this.allLocations;
    this._changeDetectorRef.markForCheck();
  }

  closeLocationPicker(): void {
    this.showLocationPicker = null;
    this.searchQuery = '';
    this._changeDetectorRef.markForCheck();
  }

  onSearchQueryChange(query: string): void {
    this.searchQuery = query;
    if (!query) {
      this.filteredLocations = this.allLocations;
    } else {
      this.filteredLocations = this.allLocations.filter(location =>
        location.name.toLowerCase().includes(query.toLowerCase()) ||
        location.category.toLowerCase().includes(query.toLowerCase())
      );
    }
    this._changeDetectorRef.markForCheck();
  }

  async adjustPrice(amount: number): Promise<void> {
    // Check if the new amount would be less than minAmount (0)
    // if (this.originalFare > this.minAmount + amount) {
    //   return; // Don't adjust if it goes negative
    // }

    // let newPrice = this.minAmount  + amount;
    this.globalTip += amount;
    if(this.globalTip<0){
      this.globalTip=0;
      return;
    }
        let newPrice = this.minAmount + amount;
    console.log("Adjusting price to",newPrice);


    await this.applyCustomPrice(newPrice)
    
    
    // Si c'est une augmentation (+5 DH), redémarrer la recherche
    //  await this.restartSearch();
    
    this._changeDetectorRef.markForCheck();
  }
  async applyGlobalPrice(amount: number): Promise<void> {
    // let newPrice = this.minAmount + amount;
    this.globalTip=amount;

    // console.log("Applying global price",newPrice);

    await this.applyCustomPrice(this.globalTip+this.originalFare,true)
    
    
    // Si c'est une augmentation (+5 DH), redémarrer la recherche
     
    
    this._changeDetectorRef.markForCheck();
  }
    calculateDurationMinutes(
      distanceM: number,
      averageSpeedKmH = 30
    ): number {
      return Math.round((distanceM / 1000 / averageSpeedKmH) * 3600);
    }

   private async restartSearch(res?: any): Promise<void> {
    // Réinitialiser les valeurs de recherche
    this.retryCount++; // Incrémenter le compteur de tentatives
    this.isSearching = true;
    this.searchTimeTotal=res.total_max_duration/ 1000;
    this.searchTimeRemaining = this.searchTimeTotal;
    this.visibleDriversCount = 0;
    this.clearAcceptedDrivers.emit();
    await this.initFromActiveTrips()
    // this.rejectedDriverIds.clear();
    
    // Réinitialiser les drivers visibles
    // this.updateVisibleDrivers();
    
    // Relancer le timer
    this.initializeSearch();
  }

  // Search Status Management Methods
  setSearchStatus(status: SearchStatus): void {
    this.currentSearchStatus = status;
    console.log('Search status updated to:', status);
    this._changeDetectorRef.markForCheck();
  }

  getSearchStatus(): SearchStatus {
    return this.currentSearchStatus;
  }

  isSearchStarted(): boolean {
    return this.currentSearchStatus === SearchStatus.STARTED;
  }

  isSearchCompleted(): boolean {
    return this.currentSearchStatus === SearchStatus.COMPLETED;
  }

  isSearchFinished(): boolean {
    return this.currentSearchStatus === SearchStatus.FINISHED;
  }

  isSearchBroadcast(): boolean {
    return this.currentSearchStatus === SearchStatus.BROADCAST;
  }

  isSearchCancelled(): boolean {
    return this.currentSearchStatus === SearchStatus.CANCELLED;
  }

  // Calculer la largeur de la barre de progression
  getProgressWidth(): number {
    let total= ((this.searchTimeTotal - this.searchTimeRemaining) / this.searchTimeTotal) * 100
    return total <= 100 ? total : 100;
  }

  // Retourne la liste des chauffeurs triés et non rejetés
  getDriversSorted(): Driver[] {
    return this.listDriversAccepted
      .filter(driver => !this.rejectedDriverIds.has(driver.offerId))
      .slice()
      .reverse(); // Nouveau à ancien
  }


  trackByLocation(index: number, location: Location): string {
    return `${location.name}-${index}`;
  }

getDriverTitleMessage(): string {
  switch (this.currentTripStatus()) {
    case 3:
      return this.translateService.translateKey('TRIP.DRIVER_ARRIVED');
    case 4:
      return this.translateService.translateKey('TRIP.inProgress');
    case 5:
      return this.translateService.translateKey('TRIP.completed');
    case 6:
      return this.translateService.translateKey('TRIP.TRIP_CANCELLED');
    case 7:
      return this.translateService.translateKey('TRIP.TRIP_EXPPIRED');
    default:
      return this.translateService.translateKey('TRIP.DRIVER_ASSIGNED');
  }
}

getDriverStatusMessage(): string {
  switch (this.currentTripStatus()) {
    case 3:
      return this.translateService.translateKey('tripStatus.arrived');
    case 4:
      return this.translateService.translateKey('tripStatus.inProgress');
    case 5:
      return this.translateService.translateKey('tripStatus.completed');
    default:
      return this.translateService.translateKey('tripStatus.waiting');
  }
}

  // Annuler la recherche (e.g., pour arrivée, tentatives épuisées, etc.)
  cancelSearch(reason?: string): void {
    // this.setSearchStatus(SearchStatus.CANCELLED);
    this.isSearching = false;
    console.log('Search cancelled. Reason:', reason || 'No reason provided');
    this._changeDetectorRef.markForCheck();
  }

  // Calculer le nombre de tentatives restantes
  getRemainingRetries(): number {
    return this.maxRetries - this.retryCount;
  }

  retourToHomeClick(): void {
    this.retourToHome.emit();
  }

  // Custom Price Input Methods
  openCustomPriceInput(): void {
    this.showCustomPriceInput = true;
    this.customPriceAmount = this.minAmount
    this._changeDetectorRef.markForCheck();
  }

  // close custom price input
  closeCustomPriceInput(): void {
    this.showCustomPriceInput = false;
    this.customPriceAmount = null;
    this._changeDetectorRef.markForCheck();
  }

  // update amount based on input
  async applyCustomPrice(price: number,isGlobal:boolean=false): Promise<void> {
      
    
    if (price !== null && price >= this.globalTip ) {
    
    
    
      // this.globalTip = this.customPriceAmount;
      try {
      let res=await firstValueFrom(this.clientService.applyCustomPrice(price,this.ticketId));
      if(res.success){
        console.log("Custom price applied successfully",res);
        this.showCustomPriceInput = false;
        await this.restartSearch(res);

      }
      
      this._changeDetectorRef.markForCheck();
    } catch (error:any) {
      let err=error.error;
      
      console.log("Error applying custom price",err);
      if(err.code=='SEARCH_IN_PROGRESS'){
        this.isSearching = true;
        this.searchTimeTotal=30
        this.searchTimeRemaining=this.searchTimeTotal
        this.showCustomPriceInput=false
        this._changeDetectorRef.markForCheck();
      }
      if(err.code=='TICKET_CANNOT_UPDATE'){

        this.errorMessage=this.translateService.translateKey('TRIP.CANNOT_UPDATE_PRICE_AFTER_DRIVER_ACCEPTED');
        this._changeDetectorRef.markForCheck();

      }
      if(err.code=='DRIVER_BLOCKED_DURING_PROCESS'){
        this.openConfirmationDialog({
          value: {
            title: this.translateService.translateKey('ERRORS.DRIVER_BLOCKED.TITLE'),
            message: this.translateService.translateKey('ERRORS.DRIVER_BLOCKED.MESSAGE'),
            icon: { name: 'heroicons_outline:exclamation-triangle', color: 'warn' },
            actions: {
              confirm: { label: 'OK', color: 'warn' },
            },
          }
        });
      }
      if(err.code=='DRIVER_UNAVAILABLE_NOW'){
        this.openConfirmationDialog({
          value: {
            title: this.translateService.translateKey('ERRORS.DRIVER_UNAVAILABLE.TITLE'),
            message: this.translateService.translateKey('ERRORS.DRIVER_UNAVAILABLE.MESSAGE'),
            icon: { name: 'heroicons_outline:exclamation-triangle', color: 'warn' },
            actions: {
              confirm: { label: 'OK', color: 'warn' },
            },
          }
        });
      }
      if(err.code=='PRICE_TOO_HIGH'){
        this.openConfirmationDialog({
          value: {
            title: this.translateService.translateKey('ERRORS.PRICE_TOO_HIGH.TITLE'),
            message: this.translateService.translateKey('ERRORS.PRICE_TOO_HIGH.MESSAGE'),
            icon: { name: 'heroicons_outline:exclamation-triangle', color: 'warn' },
            actions: {
              confirm: { label: 'OK', color: 'warn' },
            },
          }
        });  
      }
      if(err.code=='NO_DRIVERS_AVAILABLE'){
        this.errorMessage='No drivers available in your area'
      }

    }
    }
  }

  openConfirmationDialog(
    configForm: any,
    action = false,
    okCallBack = () => {},
    noCallBack = () => {}
  ): void {
    // Open the dialog and save the reference of it
    const dialogRef = this._fuseConfirmationService.open(configForm.value);

    // Subscribe to afterClosed from the dialog reference
    dialogRef.afterClosed().subscribe((result: any) => {
      console.log('DIALOG RESULT ---> ', result);
      if (result === 'confirmed' && action) {
        okCallBack();
      }
    });
  }

  loadProfile(imageName: string): string {
    return this.clientService.loadProfile(imageName);
  }

  formatDistance(distance: number): string {
    if (distance >= 1000) {
      return `${(distance / 1000).toFixed(1)} km`;
    }
    return `${Math.round(distance)} m`;
  }
  onlyNumbers(event: KeyboardEvent) {
  const allowedKeys = [
    'Backspace',
    'ArrowLeft',
    'ArrowRight',
    'Delete',
    'Tab'
  ];

  if (allowedKeys.includes(event.key)) {
    return;
  }

  // Autoriser uniquement chiffres 0–9
  if (!/^[0-9]$/.test(event.key)) {
    event.preventDefault();
  }
}

blockPasteNonNumeric(event: ClipboardEvent) {
  const pasted = event.clipboardData?.getData('text') || '';
  if (!/^\d+$/.test(pasted)) {
    event.preventDefault();
  }
}

}